import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { MetricsGrid, CategoriesChart, WaitlistPanel } from "@/components/AdminPanels";

const PW_KEY = "ideaforge_admin";

const fetchStats = (pw) => axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Password": pw } }).then((r) => r.data);

const useAdminSession = () => {
  const [pw, setPw] = useState(() => sessionStorage.getItem(PW_KEY));
  const [stats, setStats] = useState(null);
  const logout = () => { sessionStorage.removeItem(PW_KEY); setPw(null); setStats(null); };
  const login = (value) => { sessionStorage.setItem(PW_KEY, value); setPw(value); };

  useEffect(() => {
    if (!pw) return;
    fetchStats(pw).then(setStats).catch(() => { sessionStorage.removeItem(PW_KEY); setPw(null); });
  }, [pw]);

  return { pw, stats, login, logout };
};

const Login = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, null, { headers: { "X-Admin-Password": pw } });
      onLogin(pw);
    } catch {
      toast.error("Password errata");
    }
  };
  return (
    <form onSubmit={submit} className="max-w-sm mx-auto mt-24 rounded-3xl bg-[#12141F] border border-white/[0.07] p-8" data-testid="admin-login-form">
      <Lock className="w-6 h-6 text-amber-400" />
      <h1 className="font-display text-2xl font-bold mt-4">Area riservata</h1>
      <p className="text-sm text-slate-400 mt-2">Dashboard di validazione per il proprietario.</p>
      <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" data-testid="admin-password-input"
        className="w-full mt-6 rounded-xl bg-[#090A0F] border border-white/10 px-4 py-3 text-sm outline-none focus:border-amber-500/60" />
      <button type="submit" data-testid="btn-admin-login" className="w-full mt-4 rounded-full bg-amber-500 py-3 font-semibold text-slate-950 hover:bg-amber-400 transition-colors">Entra</button>
    </form>
  );
};

export default function Admin() {
  const { pw, stats, login, logout } = useAdminSession();

  if (!pw) return <Login onLogin={login} />;
  if (!stats) return <div className="text-center text-slate-400 pt-24" data-testid="admin-loading">Caricamento…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 space-y-8" data-testid="admin-dashboard">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline-label">Dashboard di validazione</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight mt-3">C'è interesse reale?</h1>
        </div>
        <button onClick={logout} data-testid="btn-admin-logout" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-rose-300 transition-colors"><LogOut className="w-4 h-4" /> Esci</button>
      </div>
      <MetricsGrid stats={stats} />
      <div className="grid lg:grid-cols-12 gap-6">
        <CategoriesChart categories={stats.categories} avgScore={stats.avg_score} />
        <WaitlistPanel waitlist={stats.waitlist} />
      </div>
    </div>
  );
}
